import React, { useState, useEffect, useContext } from 'react';
import { Row, Col, Button, Card, Spinner } from 'react-bootstrap';
import Header from '../../components/Layout/Header';
import Main from '../../components/Layout/Main';
import Select from 'react-select';
import Sidebar from '../../components/Layout/Sidebar';
import ShopsContext from '../../context/shops/shopsContext';
import CustomersContext from '../../context/otogas/customers/customersContext';
import SweetAlertWrapper from "../../components/Layout/sweetAlert";
import AddPaymentMethodModal from './addPaymentMethod';
import AddOtoshopProductModal from './addShopItem';
import SetupUsageModal from './setup';
import { validate_consumption, validateShopPaymentReference } from '../../api/otogas';

// Reusable component for validation messages
const ValidationMessage = ({ message }) => {
    if (!message) return null;
    return (
        <div className={`mt-2 ${message.type === 'success' ? 'text-success' : 'text-danger'}`}>
            {message.text}
        </div>
    );
};

const PointOfSale = () => {
    const shopsContext = useContext(ShopsContext);
    const customersContext = useContext(CustomersContext);
    const { listProducts, products, listPaymentMethods, paymentMethods, makeSale, notification, clear_notification } = shopsContext;
    const { fetchVehicles, vehicles } = customersContext;

    // State Variables
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [vehicleDetails, setVehicleDetails] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [isValidated, setIsValidated] = useState(null);
    const [vehicleOptions, setVehicleOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState({ text: "", type: "" });
    const [showAlert, setShowAlert] = useState(false);
    const [paymentReference, setPaymentReference] = useState('');
    const [referenceValid, setReferenceValid] = useState(null);
    const [validationMessages, setValidationMessages] = useState({});
    const [isFullDiscount, setIsFullDiscount] = useState(false);
    const [checkedValidation, setCheckedValidation] = useState(false);

    // Modal States
    const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] = useState(false);
    const [showAddShopProductModal, setShowAddShopProductModal] = useState(false);
    const [showSetupUsageModal, setShowSetupUsageModal] = useState(false);

    // Derived State
    const totalPrice = isFullDiscount ? 0 : (selectedProduct ? selectedProduct.price * quantity : 0);

    // Effects
    useEffect(() => {
        listProducts();
        listPaymentMethods();
    }, []);

    useEffect(() => {
        if (notification) {
            setAlertMessage({
                text: notification.text,
                type: notification.type,
            });
            setShowAlert(true);
        }
    }, [notification]);

    useEffect(() => {
        if (vehicles) {
            setVehicleOptions(vehicles.map(vehicle => ({
                value: vehicle.vehicleCode,
                label: vehicle.vehicleRegistrationNumber,
            })));
        }
    }, [vehicles]);


    useEffect(() => {
        if (selectedProduct && vehicleDetails) {
            handleValidate();
        }
    }, [selectedProduct, vehicleDetails]);

    const debouncedFetchVehicles = regNo => {
        setTimeout(() => {
            fetchVehicles(1, 10, '', regNo, '', '', true);
        }, 500);
    };

    // Handlers
    const handleValidate = async () => {
        console.log('Validating...', selectedProduct, vehicleDetails);

        if (!selectedProduct || !vehicleDetails) {
            setValidationMessages({
                vehicle: { text: 'Please select both a product and a vehicle.', type: 'danger' }
            });
            setIsValidated(false);
            return;
        }

        setValidationMessages({}); // Clear previous errors
        setIsLoading(true);

        try {
            const response = await validate_consumption(vehicleDetails.value, selectedProduct.value);
            if (response.responseCode === 1) {
                setIsValidated(true);
                setValidationMessages({
                    vehicle: { text: 'Vehicle and product combination is valid.', type: 'success' }
                });
            } else {
                setIsValidated(false);
                setValidationMessages({
                    vehicle: { text: response.responseMessage || 'Selected vehicle is not allowed for this product.', type: 'danger' }
                });
            }
        } catch {
            setIsValidated(false);
            setValidationMessages({
                vehicle: { text: 'Validation failed. Please try again.', type: 'danger' }
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleReferenceBlur = async () => {
        if (!paymentReference) {
            setValidationMessages({
                paymentReference: { text: 'Please enter a payment reference.', type: 'danger' }
            });
            setReferenceValid(false);
            return;
        }

        setIsLoading(true);

        try {
            const data = await validateShopPaymentReference(paymentReference);
            if (data.responseCode === 1 && data.responseObject?.usageBalance >= totalPrice) {
                setReferenceValid(true);
                setCheckedValidation(true);
                setValidationMessages({
                    paymentReference: { text: 'Payment reference is valid.', type: 'success' }
                });
            } else if (data.responseCode === 2) {
                setReferenceValid(false);
                setCheckedValidation(true);
                setValidationMessages({
                    paymentReference: { text: `Invalid payment reference.`, type: 'danger' }
                });
            } else {
                setReferenceValid(false);
                setCheckedValidation(true);
                setValidationMessages({
                    paymentReference: {
                        text: `Insufficient balance. Available: KSH ${data.responseObject?.usageBalance}, Required: KSH ${totalPrice}.`,
                        type: 'danger'
                    }
                });
            }
        } catch {
            setReferenceValid(false);
            setValidationMessages({
                paymentReference: { text: 'Validation error. Try again.', type: 'danger' }
            });
        } finally {
            setIsLoading(false);
        }
    };




    const handleSell = async () => {

        if (!paymentMethod) {
            setAlertMessage({ text: 'Please select a payment method.', type: 'danger' });
            setShowAlert(true);
            return;
        }

        const payload = {
            totalAmount: totalPrice,
            paymentMethod,
            vehicleCode: vehicleDetails.value,
            mpesaReference: paymentReference,
            sales: [
                {
                    itemCode: selectedProduct.value,
                    quantity,
                    price: selectedProduct.price,
                },
            ],
        };

        setIsLoading(true);

        try {
            await makeSale(payload);
            // setAlertMessage({ text: 'Sale completed successfully.', type: 'success' });

            // Reset form
            setSelectedProduct(null);
            setQuantity(1);
            setVehicleDetails(null);
            setPaymentMethod('');
            setIsValidated(null);
            setCheckedValidation(false);
            setIsFullDiscount(false);
            setPaymentReference('');
            setReferenceValid(null);
        } catch {
            setAlertMessage({ text: 'Sale failed. Please try again.', type: 'danger' });
        } finally {
            setIsLoading(false);
            setShowAlert(true);
        }
    };

    return (
        <div>
            <Header />
            <Sidebar page="pos" />
            <Main>
                <SweetAlertWrapper
                    show={showAlert}
                    title="Notification"
                    message={alertMessage.text}
                    onConfirm={() => {
                        setShowAlert(false);
                        clear_notification()

                    }}
                    confirmBtnText="OK"
                    type={alertMessage.type || "info"}
                />

                <Card className="p-3 mb-4">
                    <Row className="align-items-center">
                        <Col>
                            <h3 className="mb-0">Otoshop</h3>
                        </Col>
                        <Col className="text-end">
                            <Button
                                variant="primary"
                                className="me-2"
                                onClick={() => setShowAddShopProductModal(true)}
                            >
                                Add Product
                            </Button>
                            <Button
                                variant="secondary"
                                className="me-2"
                                onClick={() => setShowAddPaymentMethodModal(true)}
                            >
                                Add Payment Method
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => setShowSetupUsageModal(true)}
                            >
                                Setup Usage
                            </Button>
                        </Col>
                    </Row>
                </Card>

                <Row>
                    <Col md={8}>
                        <Card className="mb-4">
                            <Card.Header as="h5">Sales Form</Card.Header>
                            <Card.Body>
                                <h6>Select Product</h6>
                                <Select
                                    options={products.map(product => ({
                                        value: product.itemCode,
                                        label: product.itemName,
                                        price: product.price,
                                        hasDiscount: product.hasDiscount,
                                    }))}
                                    onChange={option => {
                                        setSelectedProduct(option);
                                        setIsValidated(false);
                                        setVehicleDetails(null);
                                        setValidationMessages({});
                                    }}
                                    placeholder="Select a product"
                                />

                                {selectedProduct && (
                                    <>
                                        <h6 className="mt-3">Select Vehicle</h6>
                                        {/* <Select
                                            options={vehicleOptions}
                                            onInputChange={debouncedFetchVehicles}
                                            onChange={option => {
                                                setVehicleDetails(option);
                                                setIsValidated(false);
                                                setValidationMessages({});
                                            }}
                                            placeholder="Search and select a vehicle"
                                        /> */}

                                        <Select
                                            options={vehicleOptions}
                                            onInputChange={debouncedFetchVehicles}
                                            onChange={(option) => {
                                                console.log('Selected vehicle:', option);
                                                setVehicleDetails(option);
                                                setValidationMessages({}); // Clear any previous validation errors
                                                if (selectedProduct && option) {
                                                    handleValidate(); // Validate immediately after selection
                                                }
                                            }}
                                            placeholder="Search and select a vehicle"
                                        />



                                        {/* {vehicleDetails && (
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="mt-2"
                                                onClick={handleValidate}
                                                disabled={isLoading || !selectedProduct || !vehicleDetails}
                                            >
                                                {isLoading ? <Spinner animation="border" size="sm" /> : 'Validate Vehicle'}
                                            </Button>
                                        )} */}

                                        <ValidationMessage message={validationMessages.vehicle} />
                                    </>
                                )}

                                {console.log('isValidated:', isValidated)}
                                {console.log('selectedProduct:', selectedProduct)}

                                {
                                    vehicleDetails && selectedProduct ? (
                                        <>
                                            {console.log('isValidated:', isValidated)}
                                            {console.log('selectedProduct:', selectedProduct)}
                                            <h6 className="mt-3">Select Payment Type</h6>
                                            <Select
                                                options={paymentMethods
                                                    .filter(method => {
                                                        if (selectedProduct?.hasDiscount) {
                                                            if (isValidated) {
                                                                // If validated and product has discount, only show "Full Discount"
                                                                return method.paymentMethod === "Full Discount";
                                                            } else {
                                                                // If not validated and product has discount, show all methods except "Full Discount"
                                                                return method.paymentMethod !== "Full Discount";
                                                            }
                                                        } else {
                                                            // If no discount, exclude "Full Discount" in all cases
                                                            return method.paymentMethod !== "Full Discount";
                                                        }
                                                    })
                                                    .map(method => ({
                                                        value: method.paymentMethod,
                                                        label: method.paymentMethod,
                                                    }))}
                                                onChange={option => {
                                                    setPaymentMethod(option?.value || '');
                                                    setIsFullDiscount(option?.value === "Full Discount");
                                                    setPaymentReference('');
                                                    setReferenceValid(null);
                                                    setValidationMessages({});
                                                }}
                                                placeholder="Select Payment Method"
                                            />
                                        </>
                                    ) : null
                                }


                                {console.log('isValidated:', isValidated)}


                                {(["Mpesa", "Wallet"].includes(paymentMethod)) && (

                                    <>
                                        <h6 className="mt-3">Payment Reference</h6>
                                        <div className="d-flex">
                                            <input
                                                type="text"
                                                value={paymentReference}
                                                onChange={(e) => {
                                                    setPaymentReference(e.target.value);
                                                    setReferenceValid(null);
                                                    setValidationMessages({});
                                                }}
                                                className="form-control"
                                                placeholder="Enter Payment Reference"
                                            />
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="ms-2"
                                                onClick={handleReferenceBlur}
                                                disabled={isLoading || !paymentReference}
                                            >
                                                {isLoading ? <Spinner animation="border" size="sm" /> : 'Validate'}
                                            </Button>
                                        </div>
                                        <ValidationMessage message={validationMessages.paymentReference} />
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="mb-4">
                            <Card.Header as="h5">Invoice Summary</Card.Header>
                            <Card.Body>
                                <p>
                                    <strong>Vehicle:</strong> {vehicleDetails?.label || 'N/A'}
                                </p>
                                <p>
                                    <strong>Payment Method:</strong> {paymentMethod || 'N/A'}
                                </p>
                                <hr />
                                <p>
                                    <strong>Product:</strong> {selectedProduct?.label || 'N/A'}
                                </p>
                                <p>
                                    <strong>Quantity:</strong> {quantity}
                                </p>
                                <p>
                                    <strong>Total Price:</strong> <span className="text-success">KSH {totalPrice}</span>
                                </p>
                                <Button
                                    variant="success"
                                    className="mt-3 w-100"
                                    onClick={handleSell}
                                    disabled={!paymentMethod || (["Mpesa", "Wallet"].includes(paymentMethod) && !referenceValid)}
                                >
                                    {isLoading ? <Spinner animation="border" size="sm" /> : 'Sell'}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <AddOtoshopProductModal show={showAddShopProductModal} handleClose={() => setShowAddShopProductModal(false)} />
                <AddPaymentMethodModal show={showAddPaymentMethodModal} handleClose={() => setShowAddPaymentMethodModal(false)} />
                <SetupUsageModal show={showSetupUsageModal} handleClose={() => setShowSetupUsageModal(false)} />
            </Main>
        </div>
    );
};

export default PointOfSale;



// "transactionId": "FMESDOCHJTNOW",
// "dateCreated": "2025-02-20T18:38:59.9933785",
// "vehicleCode": "12029",
// "vehicleRegistrationNumber": "KCT114U",
// "itemName": "Service",
// "quantity": 2,
// "price": 120,
// "paymentMethod": "Mpesa",
// "paymentType": "",
// "paymentStatus": "",
// "totalAmount": 240,
// "discount": 0